// src/pages/learning/VocabularyPage.js
import React, { useState, useEffect } from 'react';
import { ChevronRight, Volume2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const VocabularyPage = ({ onNavigate }) => {
  const { updateProgress } = useAuth();
  const [vocabularyWords, setVocabularyWords] = useState([]);
  const [currentCard, setCurrentCard] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Carregar vocabulário
  useEffect(() => {
    loadVocabulary();
  }, []);

  const loadVocabulary = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getVocabulary();
      setVocabularyWords(response.words || []);
      setError('');
    } catch (error) {
      console.error('Erro ao carregar vocabulário:', error);
      setError('Erro ao carregar vocabulário. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Salvar progresso de palavra
  const saveWordProgress = async (wordId, isCorrect) => {
    try {
      await apiService.saveWordProgress({
        word_id: wordId,
        is_correct: isCorrect
      });
    } catch (error) {
      console.error('Erro ao salvar progresso da palavra:', error);
    }
  };

  // Próximo card
  const nextCard = async () => {
    const currentWord = vocabularyWords[currentCard];
    
    // Salvar progresso da palavra atual se resposta foi mostrada
    if (showAnswer && currentWord) {
      await saveWordProgress(currentWord.id, true);
      await updateProgress(5); // 5 pontos por palavra
    }
    
    setShowAnswer(false);
    setCurrentCard((prev) => (prev + 1) % vocabularyWords.length);
  };

  // Reproduzir áudio
  const playAudio = (text) => {
    if ('speechSynthesis' in window) {
      // Parar qualquer fala em andamento
      speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'zh-CN';
      utterance.rate = 0.8; // Mais devagar para aprendizado
      speechSynthesis.speak(utterance);
    }
  };

  // Loading state
  if (isLoading) {
    return <LoadingSpinner message="Carregando vocabulário..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center max-w-md">
          <div className="text-red-500 mb-4">❌</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Ops! Algo deu errado</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadVocabulary}
            className="bg-blue-500 text-white px-6 py-2 rounded-2xl hover:bg-blue-600 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // No words state
  if (vocabularyWords.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center max-w-md">
          <div className="text-gray-400 mb-4">📚</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Nenhuma palavra encontrada</h3>
          <p className="text-gray-600 mb-4">Não há palavras disponíveis no momento.</p>
          <button
            onClick={() => onNavigate('dashboard')}
            className="bg-blue-500 text-white px-6 py-2 rounded-2xl hover:bg-blue-600 transition-colors"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentWord = vocabularyWords[currentCard];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => onNavigate('dashboard')}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-shadow"
          >
            <ChevronRight className="w-6 h-6 rotate-180" />
          </button>
          <h2 className="text-2xl font-bold text-gray-800">Vocabulário</h2>
          <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
            {currentCard + 1}/{vocabularyWords.length}
          </div>
        </div>

        {/* Flashcard */}
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
          <div className="h-64 flex flex-col justify-center">
            {!showAnswer ? (
              /* Front of card - Chinese character */
              <div>
                <div className="text-6xl font-bold text-gray-800 mb-4 select-none">
                  {currentWord.chinese}
                </div>
                <button
                  onClick={() => playAudio(currentWord.chinese)}
                  className="bg-blue-500 text-white p-3 rounded-full mx-auto hover:bg-blue-600 transition-colors shadow-lg hover:shadow-xl"
                  title="Reproduzir pronúncia"
                >
                  <Volume2 className="w-6 h-6" />
                </button>
              </div>
            ) : (
              /* Back of card - Full information */
              <div className="space-y-4">
                <div className="text-4xl font-bold text-gray-800">{currentWord.chinese}</div>
                <div className="text-2xl text-blue-600 font-semibold">{currentWord.pinyin}</div>
                <div className="text-xl text-gray-600">{currentWord.english}</div>
                {currentWord.portuguese && (
                  <div className="text-lg text-gray-500">{currentWord.portuguese}</div>
                )}
                {currentWord.category && (
                  <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {currentWord.category}
                  </div>
                )}
                <button
                  onClick={() => playAudio(currentWord.chinese)}
                  className="bg-blue-500 text-white p-3 rounded-full mx-auto hover:bg-blue-600 transition-colors shadow-lg hover:shadow-xl"
                  title="Reproduzir pronúncia"
                >
                  <Volume2 className="w-6 h-6" />
                </button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-8">
            <button
              onClick={() => setShowAnswer(!showAnswer)}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-2xl font-bold hover:bg-gray-200 transition-colors"
            >
              {showAnswer ? 'Ocultar' : 'Mostrar'}
            </button>
            <button
              onClick={nextCard}
              className="flex-1 bg-blue-500 text-white py-3 rounded-2xl font-bold hover:bg-blue-600 transition-colors"
            >
              Próximo
            </button>
          </div>

          {/* Progress indicators */}
          <div className="flex justify-center mt-4 space-x-2">
            {vocabularyWords.slice(0, 5).map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentCard % 5 ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              />
            ))}
            {vocabularyWords.length > 5 && (
              <span className="text-gray-400 text-sm ml-2">
                +{vocabularyWords.length - 5}
              </span>
            )}
          </div>
        </div>

        {/* Study Tips */}
        {!showAnswer && (
          <div className="mt-6 bg-blue-50 p-4 rounded-2xl">
            <h4 className="font-bold text-blue-800 mb-2">💡 Dica de Estudo</h4>
            <p className="text-blue-700 text-sm">
              Tente pronunciar a palavra antes de mostrar a resposta. 
              Use o botão de áudio para verificar sua pronúncia!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VocabularyPage;